import { Box } from "@chakra-ui/react";
import styled, { useTheme } from "styled-components";
import { THEME } from "../helpers/constans";
import { When } from "react-if";
import { motion } from "framer-motion";

import Truck from "../assets/garbageTruck.svg";
import TruckWithGuy from "../assets/today.svg";
import ChristmassTruckWithGuy from "../assets/lampeczki.svg";
import TruckWithLamps from "../assets/v2.svg";
import { useToday } from "../hooks/useToday";
import { Dayjs } from "dayjs";
import { AppTheme } from "../theme";

type ScheduleItem = {
  date: string;
  types: string[];
};

type CalendarProps = {
  schedule: ScheduleItem[];
  today: Dayjs;
};

const DAYS = 7;
const days = Array(DAYS).fill(0);

export function Calendar({ schedule, today }: CalendarProps) {
  const { isDecember } = useToday();
  const theme = useTheme() as AppTheme;
  const dates = schedule.map((item: ScheduleItem) => item.date);

  return (
    <Box
      css={{
        marginTop: "3rem",
        position: "absolute",
        width: "100%",
        overflow: "hidden",
        left: 0,
        right: 0,
      }}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        {days.map((_, index) => {
          const indexDay = today.add(index, "day");
          const formatedDay = indexDay.format("YYYY-MM-DD");
          const dayOfWeek = indexDay.format("dd");
          const dayOfWeekNumber = indexDay.format("DD");
          const hasGarbage = dates.includes(formatedDay);

          return (
            <CalendarCard 
              key={formatedDay}
              as={motion.div}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              $isToday={index === 0}
              $hasGarbage={hasGarbage}
              $theme={theme}
            >
              <When condition={hasGarbage}>
                <Car
                  as={motion.div}
                  animate={{ 
                    y: [0, -3, 0],
                    rotate: [0, -4, 0],
                    x: "-50%"
                  }}
                  initial={{ x: "-50%" }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <img
                    width={index === 0 ? "40px" : "36px"}
                    height={index === 0 ? "40px" : "36px"}
                    alt="Śmieciarka"
                    src={getTruckType(index, isDecember)}
                  />
                </Car>
              </When>
              <CardContent>
                <DayOfWeek $isToday={index === 0} $theme={theme}>
                  {dayOfWeek}
                </DayOfWeek>
                <DayOfWeekNumber $isToday={index === 0} $theme={theme}>
                  {dayOfWeekNumber}
                </DayOfWeekNumber>
                {hasGarbage && <GarbageIndicator $theme={theme} />}
              </CardContent>
            </CalendarCard>
          );
        })}
      </Box>
    </Box>
  );
}

function getTruckType(index: number, isDecember: boolean): string {
  if (index === 0 && isDecember) {
    return ChristmassTruckWithGuy;
  } else if (index !== 0 && isDecember) {
    return TruckWithLamps;
  } else if (index === 0 && !isDecember) {
    return TruckWithGuy;
  } else {
    return Truck;
  }
}

const Car = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DayOfWeek = styled.div<{ $isToday: boolean; $theme: AppTheme }>`
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: ${({ $isToday, $theme }) => 
    $isToday ? $theme.textReverse : $theme.primary};
  font-weight: 600;
  font-size: 13px;
  opacity: ${({ $isToday }) => $isToday ? 1 : 0.85};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DayOfWeekNumber = styled.div<{ $isToday: boolean; $theme: AppTheme }>`
  font-family: 'Outfit', sans-serif;
  font-size: 26px;
  font-weight: 700;
  margin-top: 2px;
  color: ${({ $isToday, $theme }) => 
    $isToday ? $theme.textReverse : $theme.text};
  letter-spacing: -0.02em;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const GarbageIndicator = styled.div<{ $theme: AppTheme }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $theme }) => $theme.primary};
  margin-top: 5px;
  box-shadow: 0 0 12px ${({ $theme }) => $theme.primary}60;
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { 
      opacity: 1;
      transform: scale(1);
    }
    50% { 
      opacity: 0.7;
      transform: scale(1.1);
    }
  }
`;

interface CalendarCardProps {
  $isToday: boolean;
  $hasGarbage: boolean;
  $theme: AppTheme;
}

const CalendarCard = styled.div<CalendarCardProps>`
  display: flex;
  padding: 0.75rem 0.5rem;
  margin-right: 0.4rem;
  margin-top: 1.25rem;
  margin-bottom: 1rem;
  border-radius: ${THEME.borderRadius};
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 0;
  flex: 1 1 0;
  max-width: calc((100% - 2.4rem) / 7);
  transition: all ${THEME.transitionBase};
  
  &:first-child {
    margin-left: 0;
  }
  
  &:last-child {
    margin-right: 0;
  }
  
  @media (min-width: 480px) {
    padding: 1rem;
    margin-right: 0.65rem;
    min-width: 68px;
    flex: 0 0 auto;
    max-width: none;
    
    &:last-child {
      margin-right: 0;
    }
  }
  
  background: ${({ $theme, $isToday, $hasGarbage }) => {
    if ($isToday) {
      return $theme.gradientPrimary;
    }
    return $hasGarbage 
      ? $theme.backgroundGlass
      : $theme.background2;
  }};
  
  backdrop-filter: ${({ $hasGarbage, $isToday }) => 
    $hasGarbage && !$isToday ? 'blur(12px)' : 'none'};
  
  border: ${({ $hasGarbage, $isToday, $theme }) => 
    $hasGarbage && !$isToday 
      ? `2px solid ${$theme.primary}30`
      : $isToday 
        ? 'none'
        : `1px solid ${$theme.border}`};
  
  box-shadow: ${({ $isToday, $hasGarbage, $theme }) => 
    $isToday 
      ? `0 8px 25px ${$theme.primary}40` 
      : $hasGarbage 
        ? `0 4px 15px ${$theme.primary}15` 
        : $theme.shadowSm};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ $isToday, $hasGarbage, $theme }) => 
      $isToday 
        ? `0 12px 35px ${$theme.primary}50` 
        : $hasGarbage 
          ? `0 8px 25px ${$theme.primary}25` 
          : $theme.shadowMd};
  }
`;
